<template>
  <aside class="chat-sessions" :class="{ 'chat-sessions--collapsed': collapsed }">
    <div class="chat-sessions__header">
      <button
        type="button"
        class="chat-sessions__new"
        :disabled="isLoading"
        aria-label="Start a new chat"
        @click="startNewChat"
      >
        <theme-icon icon="plus" type="icons" classes="w-4 h-4" />
        <span v-if="!collapsed">New chat</span>
      </button>

      <button
        type="button"
        class="chat-sessions__toggle"
        :aria-label="collapsed ? 'Expand session list' : 'Collapse session list'"
        @click="$emit('toggle-collapse')"
      >
        <theme-icon
          :icon="collapsed ? 'arrows-horizontal' : 'arrows-horizontal'"
          type="icons"
          classes="w-3 h-3"
        />
      </button>
    </div>

    <div v-if="!collapsed" class="chat-sessions__body">
      <p v-if="!sessions.length" class="chat-sessions__empty">
        No conversations yet.
      </p>

      <ul v-else class="chat-sessions__list">
        <li
          v-for="session in sessions"
          :key="session.id"
          :class="[
            'chat-sessions__item',
            session.id === activeId ? 'chat-sessions__item--active' : ''
          ]"
        >
          <button
            type="button"
            class="chat-sessions__select"
            @click="selectSession(session.id)"
          >
            <span class="chat-sessions__title">{{ titleFor(session) }}</span>
            <span class="chat-sessions__date">{{ relativeDate(session.updated_at) }}</span>
          </button>

          <button
            type="button"
            class="chat-sessions__delete"
            :aria-label="'Delete ' + titleFor(session)"
            @click.stop="confirmDelete(session)"
          >
            <theme-icon icon="trash" type="icons" classes="w-3 h-3" />
          </button>
        </li>
      </ul>
    </div>
  </aside>
</template>

<script>
import moment from 'moment';

/**
 * Left rail of the chat sidebar. Lists existing sessions, lets the user start
 * a fresh chat, switch to an old one, or delete one. Collapsible to a narrow
 * icon-only column when the chat area needs more room.
 */
export default {
  name: 'ChatSessionList',

  props: {
    collapsed: { type: Boolean, default: false },
  },

  computed: {
    sessions() {
      return this.$store.state.chat.sessions;
    },
    activeId() {
      return this.$store.state.chat.activeSessionId;
    },
    isLoading() {
      return this.$store.state.chat.isLoading;
    },
  },

  methods: {
    titleFor(session) {
      const t = (session.title || '').trim();
      if (!t) return 'New chat';
      return t.length > 35 ? t.slice(0, 32) + '...' : t;
    },

    relativeDate(ts) {
      if (!ts) return '';
      const m = moment(ts);
      return m.isValid() ? m.fromNow() : '';
    },

    selectSession(id) {
      if (id === this.activeId) return;
      this.$store.dispatch('chat/loadSession', id);
    },

    startNewChat() {
      this.$store.dispatch('chat/startNewChat');
    },

    async confirmDelete(session) {
      if (!window.confirm(`Delete "${this.titleFor(session)}"?`)) return;
      try {
        await this.$store.dispatch('chat/deleteSession', session.id);
      } catch {
        // Error already captured in store.error; nothing to do here.
      }
    },
  },
};
</script>
